import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogBody,
} from '@verlo/shared/components/ui/dialog';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Checkbox } from '@verlo/shared/components/ui/checkbox';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cashApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { useDrawer } from '../../contexts/DrawerContext';
import { ManagerStepUpFields, useManagerStepUp, cashError } from './ManagerStepUp';

// UK notes and coins in pence — mirrors `cash.DENOMINATIONS`; the server's list is the one used.
const PENCE = { '£50': 5000, '£20': 2000, '£10': 1000, '£5': 500, '£2': 200, '£1': 100, '50p': 50, '20p': 20, '10p': 10, '5p': 5, '2p': 2, '1p': 1 };
const p = (v) => Math.round((parseFloat(v) || 0) * 100);
const pounds = (pence) => pence / 100;

/**
 * The cash-off sheet (Carl, 18 Sep): count the drawer by denomination (or one figure),
 * take the cash tips out first, see the expected cash and the variance as you count,
 * give a reason when it's over the venue's threshold, enter the PDQ's own Z total against
 * the card takings, set tomorrow's float; the rest goes to the safe. The manager signs
 * off. Expected cash is the server's sum — this sheet only mirrors it as the count moves.
 */
const CashOffSheet = ({ open, onOpenChange, onClosed, formatCurrency }) => {
  const { user } = useAuth();
  const { drawer, session, settings, canManage, refresh } = useDrawer();
  const denominations = drawer?.denominations || Object.keys(PENCE);
  const countByDenomination = settings?.denominations !== false;

  const [mode, setMode] = useState('denominations');
  const [counts, setCounts] = useState({});
  const [declared, setDeclared] = useState('');
  const [tipsOut, setTipsOut] = useState('');
  const [reason, setReason] = useState('');
  const [pdqTotal, setPdqTotal] = useState('');
  const [floatCarried, setFloatCarried] = useState('');
  const [notes, setNotes] = useState('');
  const [openOrders, setOpenOrders] = useState(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const stepUp = useManagerStepUp({ open, needed: !canManage, siteId: user?.primary_site });

  useEffect(() => {
    if (!open) return;
    setMode(countByDenomination ? 'denominations' : 'declared');
    setCounts({}); setDeclared(''); setTipsOut(''); setReason(''); setPdqTotal(''); setNotes('');
    setFloatCarried(Number(settings?.float ?? 100).toFixed(2));
    setOpenOrders(null); setAcknowledge(false);
  }, [open, countByDenomination, settings?.float]);

  const exp = session?.expected || {};
  const summary = session?.summary || {};
  // Less what went back onto cards: a card refund goes through the terminal too, so its
  // own total is short by it (the server does the same sum: `cash.card_takings`).
  const cardRefunds = p(summary.refunds?.card);
  const cardTakings = p(summary.takings?.card) + p(summary.card_tips) - cardRefunds;
  const counted = mode === 'denominations'
    ? denominations.reduce((sum, d) => sum + (PENCE[d] || 0) * Math.max(0, parseInt(counts[d], 10) || 0), 0)
    : p(declared);
  const expected = p(exp.expected) - p(tipsOut);
  const variance = counted - expected;
  const threshold = p(settings?.variance_reason_over ?? 5);
  const overThreshold = Math.abs(variance) > threshold;
  const needsReason = overThreshold && !reason.trim();
  const pdqVariance = pdqTotal === '' ? null : p(pdqTotal) - cardTakings;
  const carried = Math.min(p(floatCarried), Math.max(0, counted));
  const toSafe = Math.max(0, counted) - carried;
  const hasCount = mode === 'denominations' ? Object.values(counts).some((v) => parseInt(v, 10) > 0) : declared !== '';

  const varianceTone = variance === 0 ? 'ok' : overThreshold ? 'stop' : 'warn';
  const varianceWord = variance === 0 ? 'Balanced' : variance > 0 ? `Over ${formatCurrency(pounds(variance))}` : `Short ${formatCurrency(pounds(-variance))}`;

  const canSubmit = !submitting && hasCount && !needsReason && stepUp.ready && !stepUp.noManagers && (!openOrders || acknowledge);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        tips_out: pounds(p(tipsOut)),
        variance_reason: reason.trim(),
        pdq_total: pdqTotal === '' ? null : pounds(p(pdqTotal)),
        float_carried: pounds(p(floatCarried)),
        notes: notes.trim(),
        acknowledge_open_orders: acknowledge,
        ...stepUp.creds(),
      };
      if (mode === 'denominations') {
        payload.denominations = Object.fromEntries(denominations.map((d) => [d, Math.max(0, parseInt(counts[d], 10) || 0)]));
      } else {
        payload.declared = pounds(p(declared));
      }
      const { data } = await cashApi.closeTill(payload);
      await refresh();
      toast.success(`Cashed off · Z ${data.z_number}`);
      onClosed?.(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === 'open_orders') {
        setOpenOrders(detail.orders || []);
      } else {
        toast.error(cashError(err, "Couldn't cash off"));
        stepUp.clearPin();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const Line = ({ label, value, sign, strong, testId }) => (
    <div className={`flex items-center justify-between py-1 text-[13.5px] ${strong ? 'font-semibold text-ink' : 'text-ink-soft'}`} data-testid={testId}>
      <span>{label}</span>
      <span className="font-mono tabular-nums">{sign}{formatCurrency(value || 0)}</span>
    </div>
  );

  const money = (setter, testId, extra = {}) => (v) => (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft">£</span>
      <Input type="number" inputMode="decimal" step="0.01" min="0" value={v} onChange={(e) => setter(e.target.value)}
        className="pl-7 h-10 font-mono-display" data-testid={testId} disabled={submitting} {...extra} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="sheet" className="max-h-[94vh]" data-testid="cash-off-sheet">
        <DialogHeader>
          <DialogTitle>Cash off · {drawer?.terminal_label || 'this till'}</DialogTitle>
          <DialogDescription>Take the cash tips out, then count what's left in the drawer.</DialogDescription>
        </DialogHeader>

        <DialogBody className="pr-1">
          {openOrders && (
            <Notice tone="warn" word="Orders" title={`${openOrders.length} order${openOrders.length === 1 ? '' : 's'} still open and unpaid`} className="mb-4"
              data-testid="cash-off-open-orders">
              Settle or void them first. Closing anyway leaves them for the next session's Z read.
              <label className="mt-2 flex items-center gap-2 text-[13px] text-ink">
                <Checkbox checked={acknowledge} onCheckedChange={(v) => setAcknowledge(!!v)} data-testid="cash-off-acknowledge" />
                Close anyway
              </label>
            </Notice>
          )}

          {/* The count a little wider than the reckoning: each row needs room for "£1,000.00". */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.15fr_1fr]">
            {/* Left: the count */}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Cash tips out first</Label>
                {money(setTipsOut, 'cash-off-tips-out')(tipsOut)}
                <p className="text-[11px] text-ink-faint">Cash tips leave the drawer before the count. Card tips are tronc: on the Z read, never in the drawer.</p>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Label className="text-xs whitespace-nowrap">The count</Label>
                {countByDenomination && (
                  <Segmented value={mode} onChange={setMode} options={[
                    { value: 'denominations', label: 'By denomination', testId: 'cash-off-mode-denominations' },
                    { value: 'declared', label: 'One figure', testId: 'cash-off-mode-declared' },
                  ]} />
                )}
              </div>

              {mode === 'denominations' ? (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5" data-testid="cash-off-denominations">
                  {denominations.map((d) => {
                    const n = Math.max(0, parseInt(counts[d], 10) || 0);
                    return (
                      <div key={d} className="flex items-center gap-1.5">
                        <span className="w-8 shrink-0 text-right font-mono text-[13px] font-semibold tabular-nums text-ink">{d}</span>
                        <Input type="number" inputMode="numeric" min="0" step="1" placeholder="0" value={counts[d] ?? ''}
                          onChange={(e) => setCounts({ ...counts, [d]: e.target.value })}
                          className="h-9 w-14 px-1 text-center font-mono-display" data-testid={`cash-off-count-${d.replace(/[£]/g, 'gbp')}`} disabled={submitting} />
                        <span className="min-w-0 flex-1 whitespace-nowrap text-right font-mono text-[12px] tabular-nums text-ink-faint">{n ? formatCurrency(pounds((PENCE[d] || 0) * n)) : ''}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {money(setDeclared, 'cash-off-declared', { autoFocus: true })(declared)}
                </div>
              )}
            </div>

            {/* Right: the reckoning */}
            <div className="space-y-4">
              <div className="rounded-lg border border-hairline bg-white px-3.5 py-2.5">
                <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">Expected in the drawer</p>
                <div className="mt-1 divide-y divide-hairline">
                  <Line label="Opening float" value={exp.float_opening} />
                  <Line label="Cash sales" value={exp.cash_sales} sign="+" />
                  {p(exp.cash_tips) > 0 && <Line label="Cash tips taken" value={exp.cash_tips} sign="+" />}
                  {p(exp.float_top_ups) > 0 && <Line label="Float top-ups" value={exp.float_top_ups} sign="+" />}
                  {p(exp.cash_refunds) > 0 && <Line label="Cash refunds" value={exp.cash_refunds} sign="−" />}
                  {p(exp.paid_outs) > 0 && <Line label="Paid out" value={exp.paid_outs} sign="−" />}
                  {p(exp.safe_drops) > 0 && <Line label="Safe drops" value={exp.safe_drops} sign="−" />}
                  {(p(exp.tips_out) > 0 || p(tipsOut) > 0) && <Line label="Tips out" value={pounds(p(exp.tips_out) + p(tipsOut))} sign="−" />}
                  <Line label="Expected" value={pounds(expected)} strong testId="cash-off-expected" />
                  <Line label="Counted" value={pounds(counted)} strong testId="cash-off-counted" />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <Signal tone={hasCount ? varianceTone : 'info'} data-testid="cash-off-variance">{hasCount ? varianceWord : 'Not counted yet'}</Signal>
                  {hasCount && overThreshold && <span className="text-[11px] text-ink-faint">Over {formatCurrency(pounds(threshold))} : say why</span>}
                </div>
              </div>

              {hasCount && overThreshold && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Why is the drawer {variance > 0 ? 'over' : 'short'}?</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="h-14 text-sm" placeholder="What happened" data-testid="cash-off-reason" disabled={submitting} />
                </div>
              )}

              <div className="rounded-lg border border-hairline bg-white px-3.5 py-2.5 space-y-2">
                <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">Card · PDQ Z read</p>
                <Line label="Card takings on the till" value={pounds(cardTakings)} />
                {p(summary.card_tips) > 0 && <p className="-mt-1 text-[11px] text-ink-faint">includes {formatCurrency(summary.card_tips)} card tips (tronc)</p>}
                {cardRefunds > 0 && <p className="-mt-1 text-[11px] text-ink-faint" data-testid="cash-off-card-refunds">after {formatCurrency(pounds(cardRefunds))} refunded to card</p>}
                <div className="space-y-1.5">
                  <Label className="text-xs">PDQ terminal total</Label>
                  {money(setPdqTotal, 'cash-off-pdq')(pdqTotal)}
                </div>
                {pdqVariance != null && (
                  <Signal tone={pdqVariance === 0 ? 'ok' : Math.abs(pdqVariance) > threshold ? 'stop' : 'warn'} data-testid="cash-off-pdq-variance">
                    {pdqVariance === 0 ? 'PDQ matches' : pdqVariance > 0 ? `PDQ over ${formatCurrency(pounds(pdqVariance))}` : `PDQ short ${formatCurrency(pounds(-pdqVariance))}`}
                  </Signal>
                )}
              </div>

              <div className="rounded-lg border border-hairline bg-white px-3.5 py-2.5 space-y-2">
                <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">Float and safe</p>
                <div className="space-y-1.5">
                  <Label className="text-xs">Float carried to tomorrow</Label>
                  {money(setFloatCarried, 'cash-off-float-carried')(floatCarried)}
                </div>
                <Line label="To the safe" value={pounds(toSafe)} strong testId="cash-off-to-safe" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="h-12 text-sm" placeholder="Anything the office should know" data-testid="cash-off-notes" disabled={submitting} />
              </div>

              <ManagerStepUpFields {...stepUp} disabled={submitting} testId="cash-off" line="The manager signs the cash-off" />
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Not yet</Button>
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={submit} disabled={!canSubmit} data-testid="cash-off-confirm">
            {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}Cash off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CashOffSheet;
